import { useState, useEffect, useRef, useCallback, useMemo } from "react";
import { Autocomplete, TextField, Icon } from "@mui/material";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import CircularProgress from "@mui/material/CircularProgress";
import Grid from "@mui/material/Grid";
import { useTheme } from "@mui/material/styles";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { BoxMinimalistic } from "@solar-icons/react";
import { parse, format } from "date-fns";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import styled from "styled-components";

import { request } from "@/Api";
import { selectHasRole, selectPersonId } from "@/auth/authSlice";
import useEntitlements from "@/auth/useEntitlements";
import ClientPopup from "@/clients/ClientPopup";
import ApiAutocomplete from "@/common/ApiAutocomplete";
import { SectionTitle, ErrorMessage } from "@/common/typography";
import { SUPPORT_EMAIL } from "@/config";
import {
    FEATURE_ACCOUNTING_STRATEGY,
    FEATURE_PROJECT_OWNER,
    FEATURE_PROJECT_TYPE,
    FEATURE_MULTI_LEGAL_ENTITY,
    FEATURE_MULTI_PERSON,
    FEATURE_PROJECT_FORECAST,
    currencies,
} from "@/constants";
import { closeProjectPopup, setError } from "@/nav/navSlice";
import ProjectForecast from "@/project/ProjectForecast";
import { baseTheme } from "@/themes";
import { getCurrencySymbol } from "@/utils";

const CustomBox = styled(Box)`
    display: flex;
    flex-direction: column;
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    width: 80%;
    max-height: 86%;
    padding: 40px 50px;
    border-radius: 20px;
    background-color: ${(props) => props.backgroundColor};
`;

const Form = styled.div`
    margin: 24px 0;
    padding-bottom: 16px;
    overflow: auto;
`;
const InputLabel = styled.div`
    padding-bottom: 4px;
    font-size: 12px;
    color: ${(props) => props.color};
`;
const CreateClient = styled.div`
    display: inline-block;
    margin-left: 6px;
    cursor: pointer;
    color: ${(props) => props.color};
`;

const requiredProjectFields = [
    "name",
    "legal_entity_id",
    "currency",
    "project_type_id",
    "client_id",
    "owner_id",
    "status_id",
    "forecast_revenue",
    "forecast_schedule",
    "forecast_certainty",
];
const requiredForecastBillFields = ["percentage", "date"];
const EMPTY_OBJECT = {};

export default function CreateProjectPopup() {
    const theme = useTheme();
    const navigate = useNavigate();
    const scrollRef = useRef();
    const dispatch = useDispatch();

    const personId = useSelector(selectPersonId);
    const isOwner = useSelector((state) =>
        selectHasRole(state, "project_owner"),
    );
    const { hasFeature } = useEntitlements();

    const hasAccountingStrategy = hasFeature(FEATURE_ACCOUNTING_STRATEGY);

    const [project, setProject] = useState(() => ({
        owner_id: isOwner ? personId : null,
        forecast_bills: [],
    }));
    const [loading, setLoading] = useState(false);
    const [errors, setErrors] = useState({});
    const [errorMessage, setErrorMessage] = useState("");
    const [clients, setClients] = useState([]);
    const [clientPopupOpen, setClientPopupOpen] = useState(false);

    const getClients = useCallback(() => {
        request.get("clients").then((response) => {
            setClients(response.data);
        });
    }, []);

    const clearFieldError = useCallback((fieldName, value) => {
        if (!value) {
            return;
        }

        setErrors((previousErrors) => {
            if (!previousErrors[fieldName]) {
                return previousErrors;
            }

            const { [fieldName]: _, ...remainingErrors } = previousErrors;
            return remainingErrors;
        });
    }, []);

    const clientNameById = useMemo(
        () =>
            clients.reduce((map, client) => {
                map[client.id] = client.name;
                return map;
            }, {}),
        [clients],
    );

    const clientOptions = useMemo(
        () => clients.map((client) => client.id),
        [clients],
    );

    const forecastData = useMemo(
        () => ({
            forecast_revenue: project.forecast_revenue || null,
            forecast_schedule: project.forecast_schedule || null,
            forecast_certainty: project.forecast_certainty || null,
            forecast_bills: project.forecast_bills || [],
        }),
        [
            project.forecast_bills,
            project.forecast_certainty,
            project.forecast_revenue,
            project.forecast_schedule,
        ],
    );

    const forecastErrors = useMemo(
        () => ({
            forecast_revenue: errors.forecast_revenue,
            forecast_schedule: errors.forecast_schedule,
            forecast_certainty: errors.forecast_certainty,
            forecast_bills: errors.forecast_bills || {},
        }),
        [
            errors.forecast_bills,
            errors.forecast_certainty,
            errors.forecast_revenue,
            errors.forecast_schedule,
        ],
    );

    const currencySymbol = useMemo(
        () => getCurrencySymbol(project.currency || "CAD"),
        [project.currency],
    );

    const setForecast = useCallback((forecast) => {
        setProject((previousProject) => ({
            ...previousProject,
            ...forecast,
        }));
    }, []);

    const mergeForecastErrors = useCallback((forecastErrors) => {
        setErrors((previousErrors) => ({
            ...previousErrors,
            ...forecastErrors,
        }));
    }, []);

    const openClientPopup = useCallback(() => {
        setClientPopupOpen(true);
    }, []);

    const noop = useCallback(() => {}, []);

    const handleClose = useCallback(() => {
        dispatch(closeProjectPopup());
    }, [dispatch]);

    const handleProjectCreate = useCallback(() => {
        const reducer = (errors, error) => (
            (errors[error] = "Field required"),
            errors
        );
        const missingRequiredFields = requiredProjectFields
            .filter((field) => !project[field])
            .reduce(reducer, {});
        const missingForecastBillFields = {};
        if (hasAccountingStrategy) {
            for (const i in project.forecast_bills) {
                const missing = requiredForecastBillFields
                    .filter((field) => !project.forecast_bills[i][field])
                    .reduce(reducer, {});
                if (Object.keys(missing).length) {
                    missingForecastBillFields[i] = missing;
                }
            }
            if (Object.keys(missingForecastBillFields).length) {
                missingRequiredFields.forecast_bills = {
                    ...missingForecastBillFields,
                };
            }
            if (Object.keys(missingRequiredFields).length) {
                setErrors(missingRequiredFields);
                return;
            }
        }

        setErrors({});
        setErrorMessage("");
        setLoading(true);

        request
            .post("projects", {
                ...project,
                model_type: hasAccountingStrategy
                    ? "project_create"
                    : "project_create_optional_forecast", // Discriminator
            })
            .then((response) => {
                let project = response.data;
                if (response.data.warning) {
                    dispatch(
                        setError({
                            message: response.data.warning,
                            severity: "warning",
                        }),
                    );
                    project = response.data.project;
                }

                navigate(`/projects/${project.id}`);
                handleClose();
            })
            .catch((error) => {
                if (error?.response?.status === 422) {
                    const details = error.response.data.detail;
                    const currentErrors = {};
                    for (const i in details) {
                        const field = details[i].loc[details[i].loc.length - 1];
                        const msg = details[i].msg;
                        if (details[i].loc[1] === "forecast_bills") {
                            if (!currentErrors.forecast_bills) {
                                currentErrors.forecast_bills = {};
                            }
                            const index = details[i].loc[2];
                            currentErrors.forecast_bills[index] = {
                                ...currentErrors.forecast_bills[index],
                                [field]: msg,
                            };
                        } else {
                            currentErrors[field] = msg;
                        }
                    }
                    setErrors(currentErrors);
                } else {
                    // console.error(error);
                    setErrorMessage(
                        `Failed to create project. Please contact support at ${SUPPORT_EMAIL}`,
                    );
                }
            })
            .finally(() => {
                setLoading(false);
            });
    }, [dispatch, handleClose, hasAccountingStrategy, navigate, project]);

    const handleInputChange = useCallback(
        (fieldName) => (event) => {
            const { value } = event.target;

            setProject((previousProject) => {
                if (previousProject[fieldName] === value) {
                    return previousProject;
                }

                return { ...previousProject, [fieldName]: value };
            });
            clearFieldError(fieldName, value);
        },
        [clearFieldError],
    );

    const handleCalendarChange = useCallback(
        (fieldName) => (newValue) => {
            let date = newValue;
            if (date != null) {
                try {
                    date = format(newValue, "y-MM-dd");
                } catch (e) {
                    date = "";
                }
            }

            setProject((previousProject) => {
                if (previousProject[fieldName] === date) {
                    return previousProject;
                }

                return { ...previousProject, [fieldName]: date };
            });
        },
        [],
    );

    const handleAutocompleteChange = useCallback(
        (fieldName) => (event, newValue) => {
            if (fieldName === "client_id") {
                const clientCurrency = clients.find(
                    (client) => newValue === client.id,
                )?.currency;

                setProject((previousProject) => {
                    if (
                        previousProject[fieldName] === newValue &&
                        previousProject.currency === clientCurrency
                    ) {
                        return previousProject;
                    }

                    return {
                        ...previousProject,
                        [fieldName]: newValue,
                        currency: clientCurrency,
                    };
                });
            } else {
                setProject((previousProject) => {
                    if (previousProject[fieldName] === newValue) {
                        return previousProject;
                    }

                    return { ...previousProject, [fieldName]: newValue };
                });
            }

            clearFieldError(fieldName, newValue);
        },
        [clearFieldError, clients],
    );

    const handleLoadApiAutocomplateResponseData = useCallback(
        (idField, isMultiChoice = false) =>
            (data) => {
                if (Array.isArray(data) && data.length === 1) {
                    setProject((projectState) => {
                        // use setState callback to set state synchronously
                        if (isMultiChoice) {
                            const currentValue = projectState[idField];
                            if (
                                Array.isArray(currentValue) &&
                                currentValue.length === 1 &&
                                currentValue[0] === data[0].id
                            ) {
                                return projectState;
                            }

                            return {
                                ...projectState,
                                [idField]: [data[0].id],
                            };
                        }

                        if (projectState[idField] === data[0].id) {
                            return projectState;
                        }

                        return {
                            ...projectState,
                            [idField]: data[0].id,
                        };
                    });
                }
            },
        [],
    );

    const inputHandlers = useMemo(
        () => ({
            name: handleInputChange("name"),
        }),
        [handleInputChange],
    );

    const calendarHandlers = useMemo(
        () => ({
            start_date: handleCalendarChange("start_date"),
            end_date: handleCalendarChange("end_date"),
            live_date: handleCalendarChange("live_date"),
        }),
        [handleCalendarChange],
    );

    const autocompleteHandlers = useMemo(
        () => ({
            client_id: handleAutocompleteChange("client_id"),
            owner_id: handleAutocompleteChange("owner_id"),
            project_type_id: handleAutocompleteChange("project_type_id"),
            status_id: handleAutocompleteChange("status_id"),
            currency: handleAutocompleteChange("currency"),
            legal_entity_id: handleAutocompleteChange("legal_entity_id"),
            person_ids: handleAutocompleteChange("person_ids"),
        }),
        [handleAutocompleteChange],
    );

    const apiAutocompleteLoadHandlers = useMemo(
        () => ({
            owner_id: handleLoadApiAutocomplateResponseData("owner_id"),
            legal_entity_id:
                handleLoadApiAutocomplateResponseData("legal_entity_id"),
            person_ids: handleLoadApiAutocomplateResponseData(
                "person_ids",
                true,
            ),
        }),
        [handleLoadApiAutocomplateResponseData],
    );

    // Auto-scroll to bottom when bills are added, to ensure they are in view
    useEffect(() => {
        if (scrollRef.current) {
            scrollRef.current.scroll({
                top:
                    scrollRef.current.scrollHeight -
                    scrollRef.current.clientHeight,
                left: 0,
                behavior: "smooth",
            });
        }
    }, [project.forecast_bills.length]);

    useEffect(() => {
        getClients();
    }, [getClients]);

    return (
        <CustomBox backgroundColor={theme.palette.background.default}>
            <SectionTitle borderColor={theme.palette.background.paper}>
                <Icon sx={{ marginRight: "3px" }}>
                    <BoxMinimalistic color="white" />
                </Icon>
                Create new Project
            </SectionTitle>

            <Form ref={scrollRef}>
                <Grid container spacing={3} width="100%">
                    <Grid item xs={12}>
                        <InputLabel color={theme.palette.text.secondary}>
                            Project Name
                        </InputLabel>
                        <TextField
                            sx={{ width: "100%" }}
                            value={project.name || ""}
                            onChange={inputHandlers.name}
                            error={"name" in errors}
                            helperText={errors["name"]}
                            data-testid="create-project-name"
                        />
                    </Grid>
                    <Grid item xs={12} sm={6} md={4}>
                        <InputLabel color={theme.palette.text.secondary}>
                            Client
                            <CreateClient
                                onClick={openClientPopup}
                                color={theme.palette.primary.main}
                            >
                                (or create one)
                            </CreateClient>
                        </InputLabel>
                        <Autocomplete
                            value={project.client_id || null}
                            disablePortal
                            options={clientOptions}
                            getOptionLabel={(id) => clientNameById[id] || ""}
                            renderInput={(params) => (
                                <TextField
                                    {...params}
                                    error={"client_id" in errors}
                                    helperText={errors["client_id"]}
                                    data-testid="create-project-client"
                                />
                            )}
                            onChange={autocompleteHandlers.client_id}
                        />
                    </Grid>
                    {hasFeature(FEATURE_PROJECT_OWNER) && (
                        <Grid item xs={12} sm={6} md={4}>
                            <InputLabel color={theme.palette.text.secondary}>
                                Producer
                            </InputLabel>
                            <ApiAutocomplete
                                value={
                                    project.owner_id ? project.owner_id : null
                                }
                                apiRoute={"persons"}
                                fields={["first_name", "last_name"]}
                                onChange={autocompleteHandlers.owner_id}
                                error={"owner_id" in errors}
                                helperText={errors["owner_id"]}
                                onLoadResponseData={
                                    apiAutocompleteLoadHandlers.owner_id
                                }
                                datatestid="create-project-producer"
                            />
                        </Grid>
                    )}
                    {hasFeature(FEATURE_PROJECT_TYPE) && (
                        <Grid item xs={12} sm={6} md={4}>
                            <InputLabel color={theme.palette.text.secondary}>
                                Project Type
                            </InputLabel>
                            <ApiAutocomplete
                                value={
                                    project.project_type_id
                                        ? project.project_type_id
                                        : null
                                }
                                apiRoute={"project-types"}
                                fields={["name"]}
                                onChange={autocompleteHandlers.project_type_id}
                                error={"project_type_id" in errors}
                                helperText={errors["project_type_id"]}
                                datatestid="create-project-type"
                            />
                        </Grid>
                    )}
                    <Grid item xs={12} sm={6} md={4}>
                        <InputLabel color={theme.palette.text.secondary}>
                            Project Status
                        </InputLabel>
                        <ApiAutocomplete
                            value={project.status_id || null}
                            apiRoute={"statuses"}
                            fields={["name"]}
                            onChange={autocompleteHandlers.status_id}
                            error={"status_id" in errors}
                            helperText={errors["status_id"]}
                            datatestid="create-project-status"
                        />
                    </Grid>
                    <Grid item xs={12} sm={6} md={4}>
                        <InputLabel color={theme.palette.text.secondary}>
                            Project Currency
                        </InputLabel>
                        <Autocomplete
                            value={project.currency || null}
                            disablePortal
                            options={currencies}
                            renderInput={(params) => (
                                <TextField
                                    {...params}
                                    error={"currency" in errors}
                                    helperText={errors["currency"]}
                                    data-testid="create-project-currency"
                                />
                            )}
                            onChange={autocompleteHandlers.currency}
                        />
                    </Grid>
                    <LocalizationProvider dateAdapter={AdapterDateFns}>
                        <Grid item xs={12} sm={6} md={4}>
                            <InputLabel color={theme.palette.text.secondary}>
                                Start Date
                            </InputLabel>
                            <DatePicker
                                value={
                                    project.start_date
                                        ? parse(
                                              project.start_date,
                                              "y-MM-dd",
                                              new Date(),
                                          )
                                        : null
                                }
                                onChange={calendarHandlers.start_date}
                                renderInput={(params) => (
                                    <TextField
                                        fullWidth
                                        {...params}
                                        error={"start_date" in errors}
                                        helperText={errors["start_date"]}
                                        data-testid="create-project-start-date"
                                    />
                                )}
                            />
                        </Grid>
                        <Grid item xs={12} sm={6} md={4}>
                            <InputLabel color={theme.palette.text.secondary}>
                                End Date
                            </InputLabel>
                            <DatePicker
                                value={
                                    project.end_date
                                        ? parse(
                                              project.end_date,
                                              "y-MM-dd",
                                              new Date(),
                                          )
                                        : null
                                }
                                onChange={calendarHandlers.end_date}
                                renderInput={(params) => (
                                    <TextField
                                        fullWidth
                                        {...params}
                                        error={"end_date" in errors}
                                        helperText={errors["end_date"]}
                                        data-testid="create-project-end-date"
                                    />
                                )}
                            />
                        </Grid>
                        <Grid item xs={12} sm={6} md={4}>
                            <InputLabel color={theme.palette.text.secondary}>
                                Live Date
                            </InputLabel>
                            <DatePicker
                                value={
                                    project.live_date
                                        ? parse(
                                              project.live_date,
                                              "y-MM-dd",
                                              new Date(),
                                          )
                                        : null
                                }
                                onChange={calendarHandlers.live_date}
                                renderInput={(params) => (
                                    <TextField
                                        fullWidth
                                        {...params}
                                        error={"live_date" in errors}
                                        helperText={errors["live_date"]}
                                        data-testid="create-project-live-date"
                                    />
                                )}
                            />
                        </Grid>
                    </LocalizationProvider>
                    {hasFeature(FEATURE_MULTI_LEGAL_ENTITY) && (
                        <Grid item xs={12} sm={6} md={4}>
                            <InputLabel color={theme.palette.text.secondary}>
                                Entity
                            </InputLabel>
                            <ApiAutocomplete
                                value={
                                    project.legal_entity_id
                                        ? project.legal_entity_id
                                        : null
                                }
                                apiRoute={"legal-entities"}
                                fields={["name"]}
                                onChange={autocompleteHandlers.legal_entity_id}
                                error={"legal_entity_id" in errors}
                                helperText={errors["legal_entity_id"]}
                                onLoadResponseData={
                                    apiAutocompleteLoadHandlers.legal_entity_id
                                }
                                datatestid="create-project-entity"
                            />
                        </Grid>
                    )}
                    {hasFeature(FEATURE_MULTI_PERSON) && (
                        <Grid item sm={12} md={8}>
                            <InputLabel color={theme.palette.text.secondary}>
                                Team Members
                            </InputLabel>
                            <ApiAutocomplete
                                value={
                                    project.person_ids ? project.person_ids : []
                                }
                                apiRoute={"persons"}
                                fields={["first_name", "last_name"]}
                                onChange={autocompleteHandlers.person_ids}
                                multiple
                                error={"person_ids" in errors}
                                helperText={errors["person_ids"]}
                                onLoadResponseData={
                                    apiAutocompleteLoadHandlers.person_ids
                                }
                                datatestid="create-project-team"
                            />
                        </Grid>
                    )}
                </Grid>

                {hasFeature(FEATURE_PROJECT_FORECAST) && (
                    <ProjectForecast
                        forecast={forecastData}
                        setForecast={setForecast}
                        errors={forecastErrors}
                        setErrors={mergeForecastErrors}
                        currency={currencySymbol}
                        cacheForecastField={noop}
                        saveForecastField={noop}
                        saveForecast={noop}
                        createMode
                        defaultExpanded
                    />
                )}
            </Form>

            <Grid container rowSpacing={3}>
                <Grid item xs={12} sm={6} md={8}>
                    <ErrorMessage>{errorMessage}</ErrorMessage>
                </Grid>
                <Grid
                    item
                    container
                    xs={12}
                    sm={6}
                    md={4}
                    columnSpacing={2}
                    justifyContent="flex-end"
                >
                    <Grid item>{loading ? <CircularProgress /> : null}</Grid>
                    <Grid item>
                        <Button
                            variant="contained"
                            color="secondary"
                            onClick={handleClose}
                            disabled={loading}
                        >
                            Cancel
                        </Button>
                    </Grid>
                    <Grid item>
                        <Button
                            variant="contained"
                            color="primary"
                            onClick={handleProjectCreate}
                            disabled={loading}
                            data-testid="create-project-submit"
                        >
                            Create
                        </Button>
                    </Grid>
                </Grid>
            </Grid>

            <ClientPopup
                isNew={true}
                popupOpen={clientPopupOpen}
                setPopupOpen={setClientPopupOpen}
                clients={clients}
                setClients={setClients}
                activeClient={EMPTY_OBJECT}
            />
        </CustomBox>
    );
}
